import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import { H1 } from '../PageBuilder/Primitives/Heading';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);
const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

import FallbackPage, { fallbackSections } from './FallbackPage';
import { ASSET_NAME } from './TermsOfServicePage.duck';

const LEADFLOW_ADDENDUM_MARKDOWN = `
*(Applicable to all users of LeadFlow features within The Modern Builder platform)*

## 1. Introduction

This Addendum applies to all users accessing LeadFlow features within the Service, including but not limited to:

- Planning data access
- Customer introductions
- In-platform messaging
- Credit-based outreach services

This Addendum forms part of the Terms and Conditions of Service and shall take precedence in relation to LeadFlow functionality.

## 2. Nature of the Service

LeadFlow is a data-driven introduction and communication platform which enables:

- Access to planning and project-related data
- Introduction of potential customers ("Leads") to suppliers
- Communication between users within the platform

The Modern Builder:

- does not act as an agent, broker, or intermediary in any transaction
- does not guarantee any commercial outcome
- is not a party to any agreement formed between users

## 3. Planning Data & Data Accuracy Disclaimer

All planning data and project-related information:

- is sourced from third-party providers and public records
- may be incomplete, delayed, or inaccurate
- is provided for informational purposes only

Users acknowledge that:

- they must conduct their own independent due diligence
- no reliance should be placed on the data for financial, legal, or commercial decisions

The Modern Builder accepts no liability for:

- decisions made based on platform data
- inaccuracies or omissions in planning data

## 4. Customer Introductions & Lead Usage

### 4.1 Lead Access

The platform may provide access to customer profiles and project information ("Leads") to suppliers.

Access to Leads is granted as a:

- limited, non-exclusive, non-transferable licence

### 4.2 Permitted Use

Leads may only be used for:

- engaging with the customer regarding their project
- providing relevant services aligned with the customer's intent

### 4.3 Restrictions

Users must not:

- share, sell, or distribute Lead information externally
- use Lead data outside the platform without consent
- attempt to bypass the platform to avoid fees or restrictions
- store or build independent databases from platform Leads

### 4.4 Platform Protection

The Modern Builder reserves the right to:

- monitor interactions
- restrict or revoke Lead access
- suspend accounts for misuse

## 5. Messaging & Communication

### 5.1 Customer Consent

By submitting their details, customers:

- consent to being contacted by relevant suppliers
- understand that suppliers may initiate contact via the platform

### 5.2 Supplier Responsibilities

Suppliers must:

- ensure all communication is relevant and proportionate
- not send unsolicited or excessive messages
- not engage in misleading, aggressive, or inappropriate conduct

### 5.3 Prohibited Behaviour

Users must not:

- send spam or bulk unsolicited outreach
- attempt to move communication off-platform prematurely
- misuse customer data for unrelated marketing

### 5.4 Monitoring

The Modern Builder may:

- monitor messaging activity
- restrict or suspend access where misuse is identified

## 6. Credits & Paid Outreach

### 6.1 Credit System

Certain features require the use of credits, including:

- digital outreach to Leads
- physical mail campaigns
- data export or API usage (where applicable)

Credits are:

- priced as displayed within the platform
- non-refundable unless required by law

### 6.2 Usage Terms

Users acknowledge:

- credits grant access to contact opportunities, not guaranteed results
- The Modern Builder does not guarantee response rates, conversion rates, or project outcomes

### 6.3 Expiry & Changes

The Modern Builder reserves the right to:

- introduce expiry periods for credits
- amend pricing with reasonable notice

## 7. No Guarantee of Outcomes

The Modern Builder does not guarantee that any Lead will respond, convert, or proceed with a project, nor does it guarantee project viability, budget accuracy, or planning success.

Users assume full responsibility for commercial decisions and engagement outcomes.

## 8. Data Protection & Roles

For the purposes of data protection:

- The Modern Builder acts as data controller for platform data
- Users accessing Leads act as independent data controllers for their own communications

Users must:

- comply with all applicable Data Protection Laws
- ensure lawful basis for any communication
- handle personal data securely and responsibly

## 9. Liability Limitation (LeadFlow Specific)

To the fullest extent permitted by law, The Modern Builder shall not be liable for:

- any loss arising from use of LeadFlow features
- any actions or omissions of other users
- any reliance on planning or project data
- any loss of business, revenue, or opportunity

## 10. Suspension & Enforcement

The Modern Builder may, at its discretion, suspend or terminate access to LeadFlow, remove access to Leads or messaging features, and revoke credits or platform privileges where a user:

- breaches this Addendum
- misuses customer data
- engages in conduct harmful to the platform

## 11. Updates

We may update this Addendum from time to time. Continued use of LeadFlow constitutes acceptance of any updated terms.
`;

const leadflowAddendumSection = {
  sectionType: 'article',
  sectionId: 'leadflow-addendum',
  appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
  title: { fieldType: 'heading1', content: 'LEADFLOW PLATFORM ADDENDUM' },
  blocks: [
    {
      blockType: 'defaultBlock',
      blockId: 'leadflow-addendum-content',
      text: {
        fieldType: 'markdown',
        content: LEADFLOW_ADDENDUM_MARKDOWN,
      },
    },
  ],
};

// This "content-only" component can be used in modals etc.
const TermsOfServiceContent = props => {
  const { inProgress, error, data } = props;

  if (inProgress) {
    return null;
  }

  // We don't want to add h1 heading twice to the HTML (SEO issue).
  // Modal's header is mapped as h2
  const hasContent = data => typeof data?.content === 'string';
  const exposeContentAsChildren = data => {
    return hasContent(data) ? { children: data.content } : {};
  };
  const CustomHeading1 = props => <H1 as="h2" {...props} />;

  const hasData = error === null && data;
  const sectionsData = hasData ? data : fallbackSections;

  return (
    <SectionBuilder
      {...sectionsData}
      options={{
        fieldComponents: {
          heading1: { component: CustomHeading1, pickValidProps: exposeContentAsChildren },
        },
        isInsideContainer: true,
      }}
    />
  );
};

// Presentational component for TermsOfServicePage
const TermsOfServicePageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  const basePageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const enrichedPageData = basePageData
    ? {
        ...basePageData,
        sections: [...(basePageData.sections || []), leadflowAddendumSection],
      }
    : basePageData;

  return (
    <PageBuilder
      pageAssetsData={enrichedPageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage />}
    />
  );
};

TermsOfServicePageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TermsOfServicePage = compose(connect(mapStateToProps))(TermsOfServicePageComponent);

const TOS_ASSET_NAME = ASSET_NAME;
export { TOS_ASSET_NAME, TermsOfServicePageComponent, TermsOfServiceContent };

export default TermsOfServicePage;
